import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronDown, ChevronRight, ClipboardList, X } from 'lucide-react';
import type { TourStop } from '../../types/deliveryTour';
import { tourPackRows, tourPackStores } from '../../types/deliveryTour';

interface TourPackListProps {
  stops: TourStop[];
}

export const TourPackList: React.FC<TourPackListProps> = ({ stops }) => {
  const [open, setOpen] = useState(false);
  const [storesOpen, setStoresOpen] = useState(false);
  const rows = tourPackRows(stops);
  const stores = tourPackStores(stops);
  if (rows.length === 0) return null;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 min-h-[44px] rounded-xl border border-gray-200 font-bold text-sm text-gray-800"
        >
          <ClipboardList size={16} /> Pack list
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[1000]" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-[1001] bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto p-4 space-y-3 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md sm:rounded-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-black text-gray-900">
                Pack list
              </Dialog.Title>
              <p className="text-xs text-gray-500 mt-0.5">What you packed</p>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="p-2 text-gray-400" aria-label="Close">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.key} className="flex justify-between gap-3 text-sm">
                <span className="text-gray-800">{row.label}</span>
                <span className="font-black text-gray-900">{row.packed}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setStoresOpen((v) => !v)}
            className="flex items-center gap-2 text-sm font-bold text-gray-700 min-h-[44px]"
          >
            {storesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Stores ({stores.length})
          </button>
          {storesOpen ? (
            <ul className="space-y-2">
              {stores.map((store) => (
                <li
                  key={store.posId}
                  className="bg-gray-50 rounded-xl px-3 py-2 text-sm"
                >
                  <p className="font-black text-gray-900">{store.name}</p>
                  {store.lines.map((line) => (
                    <p key={line.orderId} className="text-gray-600">
                      {line.qty} {line.label}
                    </p>
                  ))}
                </li>
              ))}
            </ul>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
